import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Home, Car, Landmark, BookOpen, Briefcase, ChevronRight, Shield, Clock, ThumbsUp, AlertTriangle, BadgeCheck, TrendingDown, CheckCircle2, IndianRupee, Lock, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

const products = [
  { icon: Home, title: 'Home Loan', rates: 'from 8.5% p.a.', desc: 'Realize your dream home with flexible repayment and highest LTV.', path: '/apply/home-loan', color: 'emerald', badge: 'Most Popular' },
  { icon: Briefcase, title: 'Business Loan', rates: 'from 10.5% p.a.', desc: 'Scale your operations with unsecured loans with minimum documentation.', path: '/apply/business-loan', color: 'indigo', badge: null },
  { icon: Landmark, title: 'Personal Loan', rates: 'from 10.99% p.a.', desc: 'Meet your personal expenses smoothly without any collateral.', path: '/apply/personal-loan', color: 'violet', badge: 'Quick Approval' },
  { icon: Car, title: 'Car Loan', rates: 'from 8.8% p.a.', desc: 'Drive your dream car today with up to 100% on-road funding.', path: '/apply/car-loan', color: 'sky', badge: null },
  { icon: BookOpen, title: 'Education Loan', rates: 'from 9.5% p.a.', desc: 'Fund higher education in India or abroad with easy moratoria.', path: '/apply/education-loan', color: 'amber', badge: null },
];

const colorMap = {
  emerald: { icon: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400', rate: 'text-emerald-600 dark:text-emerald-400', badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400', btn: 'hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-300 dark:hover:bg-emerald-900/20' },
  indigo: { icon: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400', rate: 'text-indigo-600 dark:text-indigo-400', badge: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-400', btn: 'hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-300 dark:hover:bg-indigo-900/20' },
  violet: { icon: 'bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400', rate: 'text-violet-600 dark:text-violet-400', badge: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-400', btn: 'hover:bg-violet-50 hover:text-violet-700 hover:border-violet-300 dark:hover:bg-violet-900/20' },
  sky: { icon: 'bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400', rate: 'text-sky-600 dark:text-sky-400', badge: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-400', btn: 'hover:bg-sky-50 hover:text-sky-700 hover:border-sky-300 dark:hover:bg-sky-900/20' },
  amber: { icon: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400', rate: 'text-amber-600 dark:text-amber-400', badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400', btn: 'hover:bg-amber-50 hover:text-amber-700 hover:border-amber-300 dark:hover:bg-amber-900/20' },
};

const highlights = [
  { icon: Shield, label: 'Trusted by 50+ Banks', sub: 'NBFC & Bank Partners' },
  { icon: Clock, label: 'Fast Processing', sub: 'Approval in 48 hours' },
  { icon: ThumbsUp, label: 'Zero Hidden Charges', sub: 'Full transparency' },
];

const avoidList = [
  'Processing fee not mentioned at the time of inquiry',
  'Insurance bundling without your written consent',
  'Pre-closure or foreclosure penalties',
  'Higher stamp duty or legal fee than standard',
  'Cheque bounce / NACH charges hidden in the schedule',
];

const protectList = [
  'Full loan cost breakdown before you apply',
  'Rate comparison across 50+ banks — at no cost to you',
  'Advisor walks you through the sanction letter clause by clause',
  'Disbursement directly to your bank account — no middlemen',
  'Post-disbursement check-in to confirm no surprise deductions',
];

const trustBadges = [
  { icon: Lock, title: 'RBI-Compliant Process', desc: 'Every lender we work with is RBI-registered. Your application is never sent to unlicensed entities.' },
  { icon: BadgeCheck, title: 'No Commission Bias', desc: 'We earn the same fee regardless of which bank you choose — so our advice is always unbiased.' },
  { icon: TrendingDown, title: 'Lowest-Cost Guarantee', desc: 'If you find a lower total cost of loan anywhere, show us and we\'ll match or beat it.' },
  { icon: Star, title: '98% Satisfaction Rate', desc: 'Based on post-disbursement surveys, 98% of our borrowers say they\'d recommend us to family and friends.' },
];

const LoanProducts = () => {
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900/50 pb-24">

      {/* ── Hero ── */}
      <div className="bg-slate-900 text-white py-20 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-950 opacity-80" />
        <div className="relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold font-['Plus_Jakarta_Sans'] mb-4">Loan Products</h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto px-4">Compare & choose the best products curated from over 50 top lenders.</p>
          <div className="flex flex-wrap justify-center gap-6 mt-10">
            {highlights.map((h) => (
              <div key={h.label} className="flex items-center gap-2.5 bg-white/8 backdrop-blur-sm rounded-2xl px-5 py-3 border border-white/10">
                <div className="h-9 w-9 rounded-xl bg-emerald-500/20 flex items-center justify-center shrink-0"><h.icon className="h-4 w-4 text-emerald-400" /></div>
                <div className="text-left"><p className="text-sm font-semibold text-white">{h.label}</p><p className="text-xs text-slate-400">{h.sub}</p></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 mt-16 max-w-6xl">

        {/* ── Loan Cards ── */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((p, i) => {
            const c = colorMap[p.color];
            return (
              <Card key={i} className="border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1.5 bg-white dark:bg-slate-800 relative overflow-hidden group">
                <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none bg-gradient-to-br from-transparent to-${p.color}-50/30 dark:to-${p.color}-900/10`} />
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${c.icon}`}><p.icon className="h-6 w-6" /></div>
                    {p.badge && <span className={`text-[10px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full ${c.badge}`}>{p.badge}</span>}
                  </div>
                  <CardTitle className="text-2xl font-bold font-['Plus_Jakarta_Sans']">{p.title}</CardTitle>
                  <CardDescription className={`font-semibold text-sm ${c.rate}`}>Interest {p.rates}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 dark:text-slate-400 mb-6 min-h-[52px] text-sm leading-relaxed">{p.desc}</p>
                  <Button variant="outline" className={`w-full justify-between shadow-sm transition-all duration-200 rounded-xl ${c.btn}`} asChild>
                    <Link to={p.path}>Apply Now <ChevronRight className="h-4 w-4" /></Link>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* ── NEW: Avoid Extra Charges Section ── */}
        <div className="mt-24 mb-16">
          <div className="text-center mb-12">
            <span className="inline-block bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">Our Cost Protection Promise</span>
            <h2 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] mb-4">We help you avoid every unnecessary charge</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">Before you sign your loan agreement, our advisors carefully review every term to protect you from hidden fees that most borrowers never notice until it's too late.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="rounded-2xl border border-rose-200 dark:border-rose-900/40 bg-rose-50/50 dark:bg-rose-900/10 p-8">
              <div className="flex items-center gap-3 mb-6">
                <AlertTriangle className="h-6 w-6 text-rose-500" />
                <h3 className="text-lg font-bold text-rose-700 dark:text-rose-400">What we help you avoid</h3>
              </div>
              <ul className="space-y-3">
                {avoidList.map((item, i) => (
                  <li key={i} className="flex items-start gap-3"><ChevronRight className="h-4 w-4 text-rose-400 mt-0.5 shrink-0" /><span className="text-sm text-slate-700 dark:text-slate-300">{item}</span></li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50/50 dark:bg-emerald-900/10 p-8">
              <div className="flex items-center gap-3 mb-6">
                <BadgeCheck className="h-6 w-6 text-emerald-500" />
                <h3 className="text-lg font-bold text-emerald-700 dark:text-emerald-400">What we guarantee instead</h3>
              </div>
              <ul className="space-y-3">
                {protectList.map((item, i) => (
                  <li key={i} className="flex items-start gap-3"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /><span className="text-sm text-slate-700 dark:text-slate-300">{item}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* ── NEW: Trust Badges ── */}
        <div className="mb-16 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 p-10 md:p-14 text-white overflow-hidden relative">
          <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 bg-emerald-500/10 rounded-full" />
          <div className="text-center mb-12 relative z-10">
            <h2 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] mb-4">Why thousands trust us with their biggest financial decisions</h2>
            <p className="text-slate-400 max-w-xl mx-auto">We've earned trust one borrower at a time — through transparency, fairness, and results.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
            {trustBadges.map((b, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors">
                <div className="h-11 w-11 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-4"><b.icon className="h-5 w-5 text-emerald-400" /></div>
                <h4 className="font-bold text-white text-sm mb-2">{b.title}</h4>
                <p className="text-slate-400 text-xs leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Bottom CTA ── */}
        <div className="rounded-3xl bg-gradient-to-r from-slate-900 to-slate-800 text-white p-10 text-center shadow-2xl">
          <h2 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] mb-3">Not sure which loan is right for you?</h2>
          <p className="text-slate-400 mb-6 max-w-md mx-auto text-sm">Speak with our expert advisors for a personalised recommendation — and a complete cost breakdown — in under 10 minutes.</p>
          <Button asChild size="lg" className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl px-8">
            <Link to="/contact">Talk to an Expert →</Link>
          </Button>
        </div>

      </div>
    </div>
  );
};

export default LoanProducts;
